// WebAuthn helpers for the Orenda secure-card-details passkey step.
//
// Orenda's fido2options come back with base64url-encoded binary fields
// (challenge, credential IDs). The browser's navigator.credentials.get()
// needs them as ArrayBuffers. After the user authenticates, the response
// has binary fields we need to encode back to base64url strings to send
// to Orenda.
//
// Spec: https://www.w3.org/TR/webauthn-2/

// base64url ↔ ArrayBuffer

// A tolerant base64url → ArrayBuffer decoder. Accepts base64url or
// standard base64, trims whitespace, handles pre-padded or unpadded.
const b64urlToBuffer = (s) => {
  if (s == null) return new Uint8Array(0).buffer;
  let str = String(s).trim();
  if (!str) return new Uint8Array(0).buffer;
  str = str.replace(/=+$/, "");
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = "=".repeat((4 - (str.length % 4)) % 4);
  const b64 = str + pad;
  if (!/^[A-Za-z0-9+/=]*$/.test(b64)) {
    throw new Error("Invalid base64 data in server response.");
  }
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

// Flexible decoder for fields that MIGHT be base64url-encoded but might
// also be a plain opaque identifier (e.g. Orenda returns user.id as
// "s|<uuid>" — a literal string, not base64).  Spec allows user.id to be
// any byte string ≤ 64 bytes, and this is Orenda's choice.
// Strategy: try base64url first; if that fails, fall back to UTF-8 bytes.
const b64urlOrUtf8ToBuffer = (s) => {
  if (s == null) return new Uint8Array(0).buffer;
  const str = String(s).trim();
  if (!str) return new Uint8Array(0).buffer;
  // If every char is a valid base64url character AND length makes sense
  // for base64, try decoding. Otherwise just encode as UTF-8.
  const looksLikeB64 = /^[A-Za-z0-9_\-+/=]+$/.test(str);
  if (looksLikeB64) {
    try { return b64urlToBuffer(str); } catch { /* fall through */ }
  }
  return new TextEncoder().encode(str).buffer;
};

const bufferToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// Convert Orenda's fido2options into the shape navigator.credentials.get expects.
const buildPublicKeyRequestOptions = (fido2options) => {
  if (!fido2options?.challenge) {
    throw new Error("Invalid passkey challenge from server.");
  }
  // Mirror the dev-only rpId override from the registration path — on
  // localhost ONLY, the browser needs rpId == current hostname. On
  // dev/staging/prod this branch does not fire and we send Orenda's
  // real rpId straight through.
  let rpId = fido2options.rpId;
  if (isLocalhost()) {
    const host = window.location.hostname;
    if (rpId && rpId !== host) {
      // eslint-disable-next-line no-console
      console.warn(
        `[orenda webauthn] DEV OVERRIDE: rewriting fido2options.rpId ` +
          `"${rpId}" → "${host}" for the passkey authentication prompt.`
      );
      rpId = host;
    }
  }
  return {
    challenge: b64urlToBuffer(fido2options.challenge),
    rpId,
    timeout: fido2options.timeout || 60000,
    userVerification: fido2options.userVerification || "preferred",
    allowCredentials: Array.isArray(fido2options.allowCredentials)
      ? fido2options.allowCredentials.map((c) => ({
          type: c.type || "public-key",
          id: b64urlToBuffer(c.id),
          transports: c.transports,
        }))
      : undefined,
  };
};

// Serialise the PublicKeyCredential returned by the browser into the JSON
// shape Orenda expects as `assertion`.
const serialiseAssertion = (credential) => ({
  id: credential.id,
  rawId: bufferToB64url(credential.rawId),
  type: credential.type,
  response: {
    clientDataJSON: bufferToB64url(credential.response.clientDataJSON),
    authenticatorData: bufferToB64url(credential.response.authenticatorData),
    signature: bufferToB64url(credential.response.signature),
    userHandle: credential.response.userHandle
      ? bufferToB64url(credential.response.userHandle)
      : null,
  },
});

// High-level: given Orenda's fido2options, prompt the browser's passkey
// (Face ID / Touch ID / Windows Hello / etc.) and return the serialised
// assertion as a JSON string ready to send to Orenda.
export const promptPasskeyAssertion = async (fido2options) => {
  if (!window.PublicKeyCredential || !navigator.credentials?.get) {
    throw new Error("Passkeys are not supported in this browser.");
  }
  const publicKey = buildPublicKeyRequestOptions(fido2options);
  const credential = await navigator.credentials.get({ publicKey });
  if (!credential) {
    throw new Error("Passkey verification was cancelled.");
  }
  return JSON.stringify(serialiseAssertion(credential));
};

// ─────────────────────────────────────────────────────────────────
// Registration (navigator.credentials.create) — used when enrolling
// a brand-new passkey for a user who doesn't have one yet.
// ─────────────────────────────────────────────────────────────────

// Orenda returns the WebAuthn PublicKeyCredentialCreationOptions with
// base64url-encoded binary fields. The browser's create() needs them as
// ArrayBuffers; this translates between the two.
//
// Note: the `user.id` field is per-spec an opaque byte string. Orenda
// chose to send it as a literal ASCII identifier like "s|<uuid>" which
// isn't valid base64 — we accept both encodings via b64urlOrUtf8ToBuffer.

// ──────────────────────────────────────────────────────────────────
// DEV-ONLY rpId override for local testing.
// Orenda's sandbox program is currently configured with
// rp.id = "bopayments.orenda-apps.com". A browser running on
// localhost refuses to create a passkey against a cross-origin rpId
// (WebAuthn security model), so we rewrite it to "localhost" here
// before passing the options to navigator.credentials.create.
//
// IMPORTANT: a passkey created with this override is bound to
// rpId=localhost and will NOT work in production against the real
// Orenda domain. The user has to re-enroll a passkey against prod.
// This block MUST NOT fire outside localhost — the guard below is
// what keeps it dormant on dev/staging/prod builds.
// ──────────────────────────────────────────────────────────────────
const DEV_LOCALHOST_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);
const isLocalhost = () => {
  if (typeof window === "undefined") return false;
  return DEV_LOCALHOST_HOSTS.has(window.location.hostname);
};
const applyDevLocalhostRpIdOverride = (publicKey) => {
  if (!isLocalhost()) return publicKey;
  const host = window.location.hostname;
  const serverRpId = publicKey?.rp?.id;
  if (!serverRpId || serverRpId === host) return publicKey;
  // eslint-disable-next-line no-console
  console.warn(
    `[orenda webauthn] DEV OVERRIDE: rewriting rp.id "${serverRpId}" ` +
      `→ "${host}" so the browser accepts the passkey registration ` +
      `on localhost. This override does NOT fire off-localhost.`
  );
  return {
    ...publicKey,
    rp: { ...publicKey.rp, id: host },
    // Any excludeCredentials in the payload were bound to the real
    // rpId; keeping them in the "localhost" rewrite would make the
    // browser throw InvalidStateError. Clear them for the dev path —
    // we only register, we don't dedupe across rpIds.
    excludeCredentials: undefined,
  };
};

const buildPublicKeyCreationOptions = (publicKeyRaw) => {
  const publicKey = applyDevLocalhostRpIdOverride(publicKeyRaw);
  if (!publicKey?.challenge || !publicKey?.user?.id) {
    throw new Error("Invalid passkey registration options from server.");
  }
  return {
    challenge: b64urlToBuffer(publicKey.challenge),
    rp: publicKey.rp,
    user: {
      id: b64urlOrUtf8ToBuffer(publicKey.user.id),
      name: publicKey.user.name,
      displayName: publicKey.user.displayName,
    },
    pubKeyCredParams: publicKey.pubKeyCredParams || [
      { type: "public-key", alg: -7 },
      { type: "public-key", alg: -257 },
    ],
    timeout: publicKey.timeout || 60000,
    attestation: publicKey.attestation || "none",
    authenticatorSelection: publicKey.authenticatorSelection,
    excludeCredentials: Array.isArray(publicKey.excludeCredentials)
      ? publicKey.excludeCredentials.map((c) => ({
          type: c.type || "public-key",
          id: b64urlToBuffer(c.id),
          transports: c.transports,
        }))
      : undefined,
    extensions: publicKey.extensions,
  };
};

// Serialise the newly-created credential into the FLAT shape Orenda's v2
// finish endpoint expects:
//   { credentialIdB64, clientDataJSON_B64, attestationObjectB64, transports }
// Doc: https://api-docs.orenda-apps.com/api-reference/passkeys/finish-adding-a-passkey-v2/
// Note the mixed casing — Orenda spells it `clientDataJSON_B64` with an
// underscore and capitalised JSON, mirroring the WebAuthn spec's field name.
const serialiseAttestation = (credential) => {
  const transports =
    typeof credential.response.getTransports === "function"
      ? credential.response.getTransports()
      : [];
  return {
    credentialIdB64: bufferToB64url(credential.rawId),
    clientDataJSON_B64: bufferToB64url(credential.response.clientDataJSON),
    attestationObjectB64: bufferToB64url(credential.response.attestationObject),
    transports: Array.isArray(transports) ? transports : [],
  };
};

// High-level: prompt the user's device to create a new passkey and return
// the serialised attestation ready to send to Orenda.
export const createPasskey = async (publicKey) => {
  if (!window.PublicKeyCredential || !navigator.credentials?.create) {
    throw new Error("Passkeys are not supported in this browser.");
  }
  const options = buildPublicKeyCreationOptions(publicKey);
  const credential = await navigator.credentials.create({ publicKey: options });
  if (!credential) {
    throw new Error("Passkey registration was cancelled.");
  }
  return serialiseAttestation(credential);
};

// Specific error messages for the registration path (different from auth).
export const describePasskeyRegistrationError = (err) => {
  const name = err?.name || "";
  if (name === "NotAllowedError") {
    return "Passkey setup was cancelled or timed out.";
  }
  if (name === "InvalidStateError") {
    return "A passkey for this device is already registered.";
  }
  if (name === "SecurityError") {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    return (
      "Could not set up a passkey. The browser's current domain " +
      (host ? `(${host}) ` : "") +
      "doesn't match what the server expected."
    );
  }
  if (name === "NotSupportedError") {
    return "This device doesn't support passkeys.";
  }
  return err?.message || "Could not set up a passkey.";
};

// Convenience: nicer error messages for the common WebAuthn failure modes.
export const describePasskeyError = (err) => {
  const name = err?.name || "";
  if (name === "NotAllowedError") {
    return "Passkey verification was cancelled or timed out.";
  }
  if (name === "InvalidStateError") {
    return "This device isn't registered. Please set up a passkey first.";
  }
  if (name === "SecurityError") {
    // Most common cause: the fido2options.rpId doesn't match the current
    // page's origin. On localhost this is almost certainly the problem;
    // on production it means the Orenda app is configured for a different
    // domain than the one we're serving from.
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    return (
      "Passkey verification failed a security check. " +
      "This usually means the browser's current domain " +
      (host ? `(${host}) ` : "") +
      "doesn't match what the passkey was registered for."
    );
  }
  if (name === "NotSupportedError") {
    return "This device doesn't support passkeys.";
  }
  return err?.message || "Could not verify passkey.";
};
